import { Cake, PartyPopper, Trophy } from 'lucide-react';
import { ModuleGate } from '@/access/Gate';
import { useSettings } from '@/access/SettingsContext';
import { useImageUrl } from '@/features/products/api';
import { daysUntil } from '@/lib/dates';
import { daysLabel, useCelebrations, useCurrentAwards, type CurrentAward } from './api';

/**
 * ימי הולדת ושנות ותק (PRD 5.8.6).
 *
 * לא מאחורי שער המודול: גם רשת שלא משתמשת בתחרויות רוצה לדעת שלמישהו
 * יש יום הולדת. המודול games שולט בנקודות ובפרסים, לא בנימוס.
 */
export function CelebrationsCard() {
  const celebrations = useCelebrations();
  // שם הרשת מגיע מההגדרות ולא מהקוד (כלל 5)
  const { brand } = useSettings();
  const data = celebrations.data;

  const items = [
    ...(data?.birthdays ?? []).map((b) => ({
      key: `b-${b.employee_id}`,
      icon: Cake,
      text: `${b.name} חוגג/ת יום הולדת`,
      when: b.date,
    })),
    ...(data?.anniversaries ?? []).map((a) => ({
      key: `a-${a.employee_id}`,
      icon: PartyPopper,
      text: `${a.name} משלים/ה ${a.years} שנים ב${brand.name}`,
      when: a.date,
    })),
  ].sort((a, b) => a.when.localeCompare(b.when));

  if (items.length === 0) return null;

  return (
    <section className="grid gap-2 rounded-lg bg-raised p-4 shadow-card">
      <h2 className="m-0 font-display text-display-m font-semibold">חוגגים</h2>
      <ul className="m-0 grid list-none gap-2 p-0">
        {items.map((item) => (
          <li key={item.key} className="flex items-center gap-2 text-body">
            <item.icon size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
            <span className="grow">{item.text}</span>
            <span className="shrink-0 text-label text-muted">
              {daysLabel(daysUntil(item.when))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** המצטיינים של החודש (PRD 5.8.5), כל עוד החודש נמשך. */
export function AwardsCard() {
  return (
    <ModuleGate module="games" fallback={null}>
      <AwardsCardInner />
    </ModuleGate>
  );
}

function AwardsCardInner() {
  const awards = useCurrentAwards();
  if (!awards.data || awards.data.length === 0) return null;

  return (
    <section className="grid gap-3 rounded-lg bg-raised p-4 shadow-card">
      <h2 className="m-0 flex items-center gap-2 font-display text-display-m font-semibold">
        <Trophy size={20} strokeWidth={1.75} aria-hidden="true" />
        המצטיינים של החודש
      </h2>
      {awards.data.map((award) => (
        <AwardRow key={award.award_id} award={award} />
      ))}
    </section>
  );
}

function AwardRow({ award }: { award: CurrentAward }) {
  const image = useImageUrl(award.image_path);

  return (
    <div className="flex items-center gap-3">
      {image.data ? (
        <img src={image.data} alt="" className="size-14 shrink-0 rounded-pill object-cover" />
      ) : (
        <span className="grid size-14 shrink-0 place-items-center rounded-pill bg-olive-soft">
          <Trophy size={24} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </span>
      )}
      <span className="grid min-w-0 gap-0.5">
        <span className="text-label text-muted">{award.type}</span>
        <span className="text-title font-medium text-ink">{award.name}</span>
        {award.reason && <span className="text-body-sm text-muted">{award.reason}</span>}
      </span>
    </div>
  );
}
